import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  ArrowLeft,
  BarChart2,
  Trophy,
  Target,
  Sparkles,
  ChevronRight,
  Flame,
  ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { API } from '../lib/api';
import { cn } from '../lib/utils';
import { ContextualState, PageLoadingState } from '../components/StatePanel';

const PlayerStats = () => {
  const { tournamentId } = useParams();
  const { getAuthHeader } = useAuth();
  const [tournament, setTournament] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(
    async (showError = true) => {
      try {
        const [tournamentRes, statsRes] = await Promise.all([
          axios.get(`${API}/tournaments/${tournamentId}`, getAuthHeader()),
          axios.get(`${API}/tournaments/${tournamentId}/stats`, getAuthHeader()),
        ]);
        setTournament(tournamentRes.data);
        setStats(statsRes.data);
      } catch (error) {
        if (showError) {
          toast.error('Failed to load stats');
        }
      } finally {
        setLoading(false);
      }
    },
    [tournamentId, getAuthHeader]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useAutoRefresh(() => fetchData(false));

  const getRoleColor = (role) => {
    switch (role) {
      case 'batsman':
        return 'bg-sky-500/10 text-sky-700 border-sky-500/20';
      case 'bowler':
        return 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20';
      case 'all-rounder':
        return 'bg-violet-500/10 text-violet-700 border-violet-500/20';
      case 'wicket-keeper':
        return 'bg-amber-500/10 text-amber-700 border-amber-500/20';
      default:
        return 'bg-slate-500/10 text-slate-700 border-slate-500/20';
    }
  };

  const summaryCards = useMemo(() => {
    const topBatsman = stats?.top_batsmen?.[0];
    const topBowler = stats?.top_bowlers?.[0];

    return [
      {
        label: 'Top Run Scorer',
        value: topBatsman ? topBatsman.stats.runs : 0,
        detail: topBatsman ? topBatsman.name : 'No data yet',
        icon: Flame,
        tone: 'border-sky-500/25 bg-sky-500/[0.05] text-sky-100',
      },
      {
        label: 'Top Wicket Taker',
        value: topBowler ? topBowler.stats.wickets : 0,
        detail: topBowler ? topBowler.name : 'No data yet',
        icon: ShieldCheck,
        tone: 'border-emerald-500/25 bg-emerald-500/[0.05] text-emerald-100',
      },
      {
        label: 'Batsmen Ranked',
        value: stats?.top_batsmen?.length || 0,
        detail: 'Leaderboard entries',
        icon: Trophy,
        tone: 'border-white/10 bg-white/[0.04]',
      },
      {
        label: 'Bowlers Ranked',
        value: stats?.top_bowlers?.length || 0,
        detail: 'Leaderboard entries',
        icon: Target,
        tone: 'border-amber-500/25 bg-amber-500/[0.05] text-amber-100',
      },
    ];
  }, [stats]);

  if (loading) {
    return (
      <Layout>
        <PageLoadingState body="single" />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 md:space-y-8">
        <section className="relative overflow-hidden rounded-[32px] border border-white/10 bg-[#08111f] text-white shadow-[0_24px_80px_rgba(3,8,20,0.45)]">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(56,189,248,0.22),transparent_35%),radial-gradient(circle_at_bottom_right,rgba(16,185,129,0.18),transparent_30%)]" />
          <div className="relative p-5 md:p-7">
            <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
              <div className="space-y-4">
                <Link
                  to={`/tournaments/${tournamentId}`}
                  className="inline-flex items-center gap-2 text-sm text-white/70 transition hover:text-white"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Tournament Hub
                </Link>
                <div className="space-y-3">
                  <Badge className="rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] uppercase tracking-[0.28em] text-white/80">
                    Stats Center
                  </Badge>
                  <div>
                    <h1 className="text-3xl font-semibold tracking-tight md:text-4xl">
                      Player performance at a glance
                    </h1>
                    <p className="mt-2 max-w-3xl text-sm text-white/72 md:text-base">
                      Follow the tournament leaders in runs and wickets through a cleaner analytics board that refreshes automatically as matches are completed.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 xl:min-w-[360px] xl:max-w-[420px]">
                <div className="rounded-2xl border border-white/10 bg-white/8 p-4 backdrop-blur">
                  <p className="text-xs uppercase tracking-[0.22em] text-white/60">Tournament</p>
                  <p className="mt-2 text-lg font-semibold">{tournament?.name}</p>
                  <p className="mt-1 text-sm text-white/65">Player stats refresh automatically</p>
                </div>
                <div className="rounded-2xl border border-sky-400/20 bg-sky-400/10 p-4 backdrop-blur">
                  <p className="text-xs uppercase tracking-[0.22em] text-sky-100/70">Analytics Feed</p>
                  <p className="mt-2 text-lg font-semibold text-sky-50">
                    {stats?.top_batsmen?.length || stats?.top_bowlers?.length ? 'Leaderboard ready' : 'Waiting on completed matches'}
                  </p>
                  <p className="mt-1 text-sm text-sky-50/75">
                    Rankings update as more scorecards are recorded in the tournament.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {summaryCards.map((card) => {
            const Icon = card.icon;
            return (
              <Card key={card.label} className={cn('border-white/10 shadow-sm', card.tone)}>
                <CardContent className="flex items-center justify-between p-5">
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                      {card.label}
                    </p>
                    <p className="mt-2 text-3xl font-semibold tracking-tight">{card.value}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{card.detail}</p>
                  </div>
                  <div className="rounded-2xl border border-current/10 bg-background/40 p-3">
                    <Icon className="h-5 w-5" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </section>

        {!stats || (stats.top_batsmen.length === 0 && stats.top_bowlers.length === 0) ? (
          <ContextualState
            icon={BarChart2}
            eyebrow="Stats Center"
            title="No statistics yet"
            description="Complete some matches to unlock the player leaderboard and performance analytics."
            data-testid="no-stats-card"
          />
        ) : (
          <section className="grid gap-6 xl:grid-cols-[1.35fr_0.65fr]">
            <div className="space-y-6">
              <Card className="border-white/10 shadow-sm">
                <CardContent className="p-5">
                  <Tabs defaultValue="batsmen" className="w-full" data-testid="stats-tabs">
                    <TabsList className="mb-6 grid w-full grid-cols-2">
                      <TabsTrigger value="batsmen" className="gap-2">
                        <Trophy className="h-4 w-4" />
                        Top Batsmen
                      </TabsTrigger>
                      <TabsTrigger value="bowlers" className="gap-2">
                        <Target className="h-4 w-4" />
                        Top Bowlers
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="batsmen" className="space-y-4" data-testid="batsmen-list">
                      {stats.top_batsmen.map((player, index) => (
                        <LeaderboardCard
                          key={player.id}
                          index={index}
                          player={player}
                          getRoleColor={getRoleColor}
                          mode="batting"
                        />
                      ))}
                    </TabsContent>

                    <TabsContent value="bowlers" className="space-y-4" data-testid="bowlers-list">
                      {stats.top_bowlers.map((player, index) => (
                        <LeaderboardCard
                          key={player.id}
                          index={index}
                          player={player}
                          getRoleColor={getRoleColor}
                          mode="bowling"
                        />
                      ))}
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <Card className="border-white/10 bg-[#091222] text-white shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Sparkles className="h-5 w-5 text-cyan-300" />
                    Stats Insights
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm text-white/72">
                  <div className="rounded-2xl border border-white/10 bg-white/6 p-4">
                    <p className="text-xs uppercase tracking-[0.18em] text-white/55">Leaderboard pulse</p>
                    <p className="mt-2 text-base font-semibold text-white">
                      Batting and bowling rankings update from the same tournament score data
                    </p>
                    <p className="mt-1">
                      As completed matches increase, this screen becomes the quickest view of who is driving the competition.
                    </p>
                  </div>

                  <div className="space-y-3 rounded-2xl border border-white/10 bg-white/6 p-4">
                    <p className="text-xs uppercase tracking-[0.18em] text-white/55">Reading cues</p>
                    <div className="space-y-3">
                      <InsightRow
                        icon={Flame}
                        title="Batting output"
                        description="Runs, balls faced, and strike rate show who is scoring fastest and most consistently."
                      />
                      <InsightRow
                        icon={ShieldCheck}
                        title="Bowling pressure"
                        description="Wickets, overs, and economy expose who is controlling innings most effectively."
                      />
                      <InsightRow
                        icon={BarChart2}
                        title="Live refresh"
                        description="The leaderboard refreshes automatically, so new completed scorecards flow through without a manual reload."
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-white/10 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Quick Links</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <QuickLink
                    to={`/tournaments/${tournamentId}`}
                    title="Tournament hub"
                    description="Return to the main control and overview screen."
                  />
                  <QuickLink
                    to={`/tournaments/${tournamentId}/points-table`}
                    title="Standings"
                    description="Compare the leaderboard with the current team table."
                  />
                  <QuickLink
                    to={`/tournaments/${tournamentId}/matches`}
                    title="Fixtures"
                    description="Check which matches can still reshape the player rankings."
                  />
                </CardContent>
              </Card>
            </div>
          </section>
        )}
      </div>
    </Layout>
  );
};

const LeaderboardCard = ({ player, index, getRoleColor, mode }) => {
  const medalTone =
    index === 0
      ? 'bg-yellow-500/15 text-yellow-700'
      : index === 1
        ? 'bg-slate-400/15 text-slate-700'
        : index === 2
          ? 'bg-amber-700/15 text-amber-700'
          : 'bg-muted text-muted-foreground';

  const battingStrikeRate =
    player.stats.balls_faced > 0
      ? ((player.stats.runs / player.stats.balls_faced) * 100).toFixed(1)
      : '0.0';

  const bowlingEconomy =
    player.stats.balls_bowled > 0
      ? (player.stats.runs_given / (player.stats.balls_bowled / 6)).toFixed(2)
      : '0.00';

  return (
    <Card
      className="border-white/10 shadow-sm transition duration-200 hover:-translate-y-0.5 hover:shadow-md"
      data-testid={`${mode === 'batting' ? 'batsman' : 'bowler'}-card-${player.id}`}
    >
      <CardContent className="p-4 md:p-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-start gap-4">
            <div
              className={cn(
                'flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-lg font-semibold',
                medalTone
              )}
            >
              {index + 1}
            </div>
            <div>
              <p className="text-lg font-semibold tracking-tight">{player.name}</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="text-sm text-muted-foreground">{player.team_name}</span>
                <Badge variant="outline" className={cn('text-xs', getRoleColor(player.role))}>
                  {player.role}
                </Badge>
              </div>
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-2xl border border-border/70 bg-background/70 px-4 py-3 text-left md:min-w-[140px] md:text-right">
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                {mode === 'batting' ? 'Runs' : 'Wickets'}
              </p>
              <p className="mt-1 text-3xl font-semibold tracking-tight">
                {mode === 'batting' ? player.stats.runs : player.stats.wickets}
              </p>
            </div>
            <div className="rounded-2xl border border-border/70 bg-background/70 px-4 py-3 text-left md:min-w-[170px] md:text-right">
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                {mode === 'batting' ? 'Efficiency' : 'Control'}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {mode === 'batting'
                  ? `${player.stats.balls_faced} balls • SR ${battingStrikeRate}`
                  : `${Math.floor(player.stats.balls_bowled / 6)}.${player.stats.balls_bowled % 6} overs • Econ ${bowlingEconomy}`}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const InsightRow = ({ icon: Icon, title, description }) => (
  <div className="flex items-start gap-3">
    <div className="rounded-xl border border-white/10 bg-white/8 p-2">
      <Icon className="h-4 w-4 text-white" />
    </div>
    <div>
      <p className="font-medium text-white">{title}</p>
      <p className="mt-1 text-sm text-white/65">{description}</p>
    </div>
  </div>
);

const QuickLink = ({ to, title, description }) => (
  <Link
    to={to}
    className="flex items-center justify-between rounded-2xl border border-border bg-card px-4 py-3 transition hover:border-primary/30 hover:bg-primary/[0.04]"
  >
    <div>
      <p className="font-medium">{title}</p>
      <p className="mt-1 text-sm text-muted-foreground">{description}</p>
    </div>
    <ChevronRight className="h-4 w-4 text-muted-foreground" />
  </Link>
);

export default PlayerStats;
